import { useEffect, useRef } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import am4lang_es_ES from '@amcharts/amcharts4/lang/es_ES';
import { mixColors } from '../../helpers/common';
import { formattingSeries } from './helpers';

export default function DualChart({
  chartData,
  chartData2,
  chartOptions,
  zoomBar,
  ky,
  auxHeight,
  granularityDinamicDash,
  tz,
  width = '100%',
  // ✅ NUEVO: props para sincronizar zoom
  sharedZoom,
  onZoomChange,
}) {
  const chartRef = useRef(null);
  const dateAxisRef = useRef(null);

  const ignoreZoomEventRef = useRef(false);
  const unlockTimerRef = useRef(null);

  // ✅ CREA / RECREA el chart SOLO cuando cambian datos/opciones (NO por zoom)
  useEffect(() => {
    const { variable, variable2, showSecondary } = chartOptions;

    am4core.useTheme(am4themes_animated);

    const chartId = `instantaneos${ky ? ky : ''}`;
    let chart = am4core.create(chartId, am4charts.XYChart);
    chartRef.current = chart;

    // Ocultar logo
    if (chart?.logo) chart.logo.disabled = true;

    chart.fontSize = 11;
    chart.language.locale = am4lang_es_ES;
    chart.dateFormatter.timezone = tz;
    chart.dateFormatter.inputDateFormat = 'yyyy-MM-ddTHH:mm:ssZ';

    // Export (cuando NO hay zoomBar)
    if (!zoomBar) {
      chart.exporting.menu = new am4core.ExportMenu();
      chart.exporting.adapter.add('data', function () {
        const myData = [];
        chart.series.each(function (series) {
          for (let i = 0; i < series.data.length; i++) {
            series.data[i].name = series.name;
            myData.push(series.data[i]);
          }
        });
        return { data: myData };
      });

      chart.exporting.menu.items = [
        {
          label: '...',
          menu: [
            { type: 'pdf', label: 'PDF' },
            { type: 'csv', label: 'CSV' },
            { type: 'xlsx', label: 'XLSX' },
            { type: 'print', label: 'Imprimir' },
          ],
        },
      ];

      chart.legend = new am4charts.Legend();
    }

    // AXIS X
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxisRef.current = dateAxis;

    dateAxis.renderer.grid.template.location = 0;
    dateAxis.baseInterval = { timeUnit: 'minute', count: 1 };
    dateAxis.tooltipDateFormat = 'EEE dd/MM/yyyy HH:mm';
    dateAxis.periodChangeDateFormats.setKey('hour', 'EEE dd HH:mm');
    dateAxis.dateFormats.setKey('day', 'EEE dd MMM');
    dateAxis.periodChangeDateFormats.setKey('day', 'EEE dd');

    // Min/Max por fechas elegidas
    const d1 = new Date(chartOptions.selectedInitialDate);
    d1.setHours(0, 0, 0, 0);
    const d2 = new Date(chartOptions.selectedEndDate);
    d2.setHours(23, 59, 0, 0);
    dateAxis.min = d1.getTime();
    dateAxis.max = d2.getTime();

    // AXIS Y primario
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.tooltip.disabled = true;
    valueAxis.zIndex = 1;
    valueAxis.renderer.baseGrid.disabled = true;
    valueAxis.renderer.inside = false;
    valueAxis.height = showSecondary ? am4core.percent(60) : am4core.percent(100);
    valueAxis.renderer.labels.template.verticalCenter = 'bottom';
    valueAxis.renderer.labels.template.padding(2, 2, 2, 2);
    valueAxis.renderer.fontSize = '0.8em';
    valueAxis.renderer.gridContainer.background.fillOpacity = 0.05;
    dateAxis.renderer.grid.template.strokeWidth = 1;
    valueAxis.renderer.grid.template.strokeWidth = 1;

    // AXIS Y secundario
    const valueAxis2 = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis2.tooltip.disabled = true;
    valueAxis2.marginTop = 20;
    valueAxis2.renderer.baseGrid.disabled = true;
    valueAxis2.renderer.inside = true;
    valueAxis2.height = am4core.percent(40);
    valueAxis2.zIndex = 3;
    valueAxis2.renderer.labels.template.verticalCenter = 'bottom';
    valueAxis2.renderer.labels.template.padding(2, 2, 2, 2);
    valueAxis2.renderer.fontSize = '0.8em';

    const interfaceColors = new am4core.InterfaceColorSet();
    if (showSecondary) {
      valueAxis.renderer.gridContainer.background.fill = interfaceColors.getFor(
        'alternativeBackground'
      );
      valueAxis2.renderer.gridContainer.background.fill = interfaceColors.getFor(
        'alternativeBackground'
      );
    }
    valueAxis2.renderer.gridContainer.background.fillOpacity = 0.05;
    valueAxis2.renderer.grid.template.strokeWidth = 1;

    // Scrollbar X (zoom/move)
    chart.scrollbarX = new am4charts.XYChartScrollbar();
    chart.scrollbarX.parent = chart.bottomAxesContainer;
    chart.scrollbarX.strokeWidth = 1;
    if (zoomBar) chart.scrollbarX.disabled = true;

    // Cursor
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.strokeWidth = 0;
    chart.strokeWidth = 2;

    // ✅ Emitir zoom al padre (guardar disposers para limpiar)
    let zoomDisp1 = null;
    let zoomDisp2 = null;

    if (onZoomChange) {
      const emitZoom = () => {
        if (ignoreZoomEventRef.current) return;

        // dateAxis.minZoomed / maxZoomed son timestamps
        const from = dateAxis.minZoomed;
        const to = dateAxis.maxZoomed;

        if (typeof from === 'number' && typeof to === 'number') {
          onZoomChange({ from, to });
        }
      };

      zoomDisp1 = dateAxis.events.on('selectionextremeschanged', emitZoom);
      zoomDisp2 = dateAxis.events.on('extremeschanged', emitZoom);
    }

    const assignSerieValues = (serie) => {
      const node = serie?.node;
      const tags = serie?.tags;
      const nodeName = node?.nombre;
      const tagName = tags?.length > 0 ? tags[0]?.nombre : '';
      const nodeColor = node?.color;
      const tagColor = tags?.length > 0 ? tags[0]?.color : '';
      const serieName = tagName ? `${nodeName} + ${tagName}` : nodeName;
      const chartColor = mixColors(nodeColor, tagColor);
      return { serieName, chartColor };
    };

    const gapCalc = () => {
      let res = 20;
      if (granularityDinamicDash) res = Number(granularityDinamicDash) * 4;
      return res;
    };

    function addSeries(serieName, serieData, serieColor, type, format, gap = 20) {
      if (!serieData) return;

      const series = chart.series.push(new am4charts.LineSeries());

      const numberFormatter = new am4core.NumberFormatter();
      numberFormatter.numberFormat = format;
      series.numberFormatter = numberFormatter;

      if (type === 'secondary') {
        series.yAxis = valueAxis2;
        valueAxis2.numberFormatter = numberFormatter;
      } else {
        series.yAxis = valueAxis;
        valueAxis.numberFormatter = numberFormatter;
      }

      series.connect = false;
      series.autoGapCount = gap;

      series.dataFields.valueY = 'valor';
      series.dataFields.dateX = 'timestamp';

      series.data = serieData;
      series.name = serieName;
      series.stroke = am4core.color(serieColor);

      series.tooltipText = `{valueY} - ${serieName}`;
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = am4core.color(serieColor);

      const circleBullet = series.bullets.push(new am4charts.CircleBullet());
      circleBullet.circle.fill = am4core.color('#fff');

      series.minBulletDistance = 15;

      chart.scrollbarX.series.push(series);
    }

    function updateChart(variableKey, axis, serie, serieName, chartColor, phases, type) {
      switch (variableKey) {
        case 'TENSION_POR_FASE':
          axis.title.text = 'Tensión por fase';
          for (let i = 0; i < phases; i++) {
            const phaseName = i === 0 ? 'R' : i === 1 ? 'S' : 'T';
            const colorOffset = i === 0 ? '' : i === 1 ? '60' : '90';
            addSeries(
              `Fase ${phaseName} (${serieName})`,
              serie?.data?.[`V_${phaseName}`],
              chartColor + colorOffset,
              type,
              '#.##V',
              gapCalc()
            );
          }
          break;

        case 'CORRIENTE_POR_FASE':
          axis.title.text = 'Corriente por fase';
          for (let i = 0; i < phases; i++) {
            const phaseName = i === 0 ? 'R' : i === 1 ? 'S' : 'T';
            const colorOffset = i === 0 ? '' : i === 1 ? '60' : '90';
            addSeries(
              `Fase ${phaseName} (${serieName})`,
              serie?.data?.[`I_${phaseName}`],
              chartColor + colorOffset,
              type,
              `#.##'A`,
              gapCalc()
            );
          }
          break;

        case 'POTENCIA_ACTIVA_TOTAL':
          axis.title.text = 'Potencia activa total';
          addSeries(serieName, serie?.data?.P, chartColor, type, '#.##aW', gapCalc());
          break;

        case 'POTENCIA_ACTIVA_POR_FASE': {
          axis.title.text = 'Potencia activa por fase';
          const phasesPlusTotal = (phases || 0) + 1;

          for (let i = 0; i < phasesPlusTotal; i++) {
            let phaseName = '';
            let label = '';
            let colorOffset = '';

            if (i === 0) {
              phaseName = 'P_R';
              label = 'Fase R';
              colorOffset = '60';
            }
            if (i === 1) {
              phaseName = 'P_S';
              label = 'Fase S';
              colorOffset = '65';
            }
            if (i === 2) {
              phaseName = 'P_T';
              label = 'Fase T';
              colorOffset = '70';
            }
            if (i === 3) {
              phaseName = 'P';
              label = 'Total';
              colorOffset = '90';
            }

            addSeries(
              `${label} (${serieName})`,
              serie?.data?.[phaseName],
              chartColor + colorOffset,
              type,
              '#.##aW',
              gapCalc()
            );
          }
          break;
        }

        case 'POTENCIA_REACTIVA_TOTAL':
          axis.title.text = 'Potencia reactiva total';
          addSeries(serieName, serie?.data?.Q, chartColor, type, '#.##aVAr', gapCalc());
          break;

        case 'POTENCIA_REACTIVA_POR_FASE': {
          axis.title.text = 'Potencia reactiva por fase';
          const phasesPlusTotal = (phases || 0) + 1;

          for (let i = 0; i < phasesPlusTotal; i++) {
            let phaseName = '';
            let label = '';
            let colorOffset = '';

            if (i === 0) {
              phaseName = 'Q_R';
              label = 'Fase R';
              colorOffset = '60';
            }
            if (i === 1) {
              phaseName = 'Q_S';
              label = 'Fase S';
              colorOffset = '65';
            }
            if (i === 2) {
              phaseName = 'Q_T';
              label = 'Fase T';
              colorOffset = '70';
            }
            if (i === 3) {
              phaseName = 'Q';
              label = 'Total';
              colorOffset = '90';
            }

            addSeries(
              `${label} (${serieName})`,
              serie?.data?.[phaseName],
              chartColor + colorOffset,
              type,
              '#.##aVAr',
              gapCalc()
            );
          }
          break;
        }

        case 'POTENCIA_ADQUIRIDA_TOTAL':
          axis.title.text = 'Potencia adquirida total';
          addSeries(
            serieName,
            serie?.data?.KW?.map((item) => ({ ...item, valor: item.valor * 1000 })),
            chartColor,
            type,
            '#.##aW',
            180
          );
          break;

        case 'ENERGIA_TOTAL_POR_HORA':
        case 'ENERGIA_ACTIVA_TOTAL':
          axis.title.text =
            variableKey === 'ENERGIA_TOTAL_POR_HORA'
              ? 'Energia total por hora'
              : 'Energia activa total';
          addSeries(
            serieName,
            serie?.data?.KWH?.map((item) => ({ ...item, valor: item.valor * 1000 })),
            chartColor,
            type,
            '#.##aWh',
            180
          );
          break;

        case 'TEMPERATURA_TOTAL':
          axis.title.text = 'Temperatura';
          addSeries(serieName, serie?.data?.TEMP, chartColor, type, '#.## °C', 180);
          break;

        case 'PRESION_TOTAL':
          axis.title.text = 'Presión';
          addSeries(serieName, serie?.data?.PRESSURE, chartColor, type, '# hPa', 180);
          break;

        case 'HUMEDAD_TOTAL':
          axis.title.text = 'Humedad Total';
          addSeries(serieName, serie?.data?.HUMIDITY, chartColor, type, "#.## '%", 180);
          break;

        case 'ENERGIA_ACTIVA_IMPORTADA_TOTAL':
          axis.title.text = 'Energía activa importada Total';
          addSeries(
            serieName,
            formattingSeries(serie?.data?.KWH_I, variableKey, 'INSTANTANEOS'),
            chartColor,
            type,
            '#.##aWh',
            180
          );
          break;

        case 'ENERGIA_ACTIVA_EXPORTADA_TOTAL':
          axis.title.text = 'Energía activa exportada total';
          addSeries(
            serieName,
            formattingSeries(serie?.data?.KWH_E, variableKey, 'INSTANTANEOS'),
            chartColor,
            type,
            '#.##aWh',
            180
          );
          break;

        case 'ENERGIA_ACTIVA_GENERADA_TOTAL':
          axis.title.text = 'Energía activa generada total';
          addSeries(
            serieName,
            formattingSeries(serie?.data?.KWH_G, variableKey, 'INSTANTANEOS'),
            chartColor,
            type,
            '#.##aWh',
            180
          );
          break;

        case 'ENERGIA_ACTIVA_CONSUMIDA_TOTAL':
          axis.title.text = 'Energía activa consumida total';
          addSeries(
            serieName,
            formattingSeries(serie?.data?.KWH_C, variableKey, 'INSTANTANEOS'),
            chartColor,
            type,
            '#.##aWh',
            180
          );
          break;

        case 'ENERGIA_ACTIVA_AUTOCONSUMIDA_TOTAL':
          axis.title.text = 'Energía activa autoconsumida total';
          addSeries(
            serieName,
            formattingSeries(serie?.data?.KWH_AC, variableKey, 'INSTANTANEOS'),
            chartColor,
            type,
            '#.##aWh',
            180
          );
          break;

        case 'POTENCIA_ACTIVA_GENERADA_TOTAL':
          axis.title.text = 'Potencia activa generada total';
          addSeries(
            serieName,
            formattingSeries(serie?.data?.P_G, variableKey, 'INSTANTANEOS'),
            chartColor,
            type,
            '#.##aW',
            180
          );
          break;

        case 'POTENCIA_ACTIVA_CONSUMIDA_TOTAL':
          axis.title.text = 'Potencia activa consumida total';
          addSeries(
            serieName,
            formattingSeries(serie?.data?.P_C, variableKey, 'INSTANTANEOS'),
            chartColor,
            type,
            '#.##aWh',
            180
          );
          break;

        default:
          break;
      }
    }

    // SERIES primarias
    (chartData || []).forEach((serie) => {
      const { chartColor, serieName } = assignSerieValues(serie);
      const phases = serie?.medidores?.[0]?.count_phases;
      updateChart(variable, valueAxis, serie, serieName, chartColor, phases, 'primary');
    });

    // SERIES secundarias
    if (showSecondary) {
      chart.leftAxesContainer.layout = 'vertical';
      (chartData2 || []).forEach((serie) => {
        const { chartColor, serieName } = assignSerieValues(serie);
        const phases = serie?.medidores?.[0]?.count_phases;
        updateChart(
          variable2,
          valueAxis2,
          serie,
          serieName,
          chartColor,
          phases,
          'secondary'
        );
      });
    }

    return () => {
      // limpiar timer si existiera
      if (unlockTimerRef.current) {
        clearTimeout(unlockTimerRef.current);
        unlockTimerRef.current = null;
      }

      // limpiar listeners (si fueron creados)
      try {
        if (zoomDisp1?.dispose) zoomDisp1.dispose();
        if (zoomDisp2?.dispose) zoomDisp2.dispose();
      } catch (_) {}

      // dispose chart
      try {
        chart.dispose();
      } catch (_) {}

      chartRef.current = null;
      dateAxisRef.current = null;
    };
    // eslint-disable-next-line
  }, [chartData, chartData2, chartOptions, granularityDinamicDash, tz, zoomBar, ky]);

  // ✅ EFECTO SEPARADO: aplicar el zoom sin recrear el chart
  useEffect(() => {
    const chart = chartRef.current;
    const dateAxis = dateAxisRef.current;

    if (!chart || !dateAxis) return;
    if (!sharedZoom?.from || !sharedZoom?.to) return;

    // cancelar unlock anterior
    if (unlockTimerRef.current) {
      clearTimeout(unlockTimerRef.current);
      unlockTimerRef.current = null;
    }

    const applyZoom = () => {
      // si el chart ya murió, no hagas nada
      if (!chartRef.current || !dateAxisRef.current) return;

      ignoreZoomEventRef.current = true;

      try {
        dateAxis.zoomToDates(
          new Date(sharedZoom.from),
          new Date(sharedZoom.to),
          false,
          true
        );
      } catch (_) {
        // si amCharts está en un estado intermedio, evitamos crashear
      }

      unlockTimerRef.current = setTimeout(() => {
        ignoreZoomEventRef.current = false;
        unlockTimerRef.current = null;
      }, 0);
    };

    // Si todavía no está “ready”, aplicalo cuando esté listo
    if (chart.inited) {
      applyZoom();
    } else {
      chart.events.once('ready', applyZoom);
    }
  }, [sharedZoom]);

  return (
    <div
      id={`instantaneos${ky ? ky : ''}`}
      style={
        auxHeight
          ? { height: auxHeight }
          : {
              height: chartOptions.showSecondary ? '700px' : '500px',
              width: width,
            }
      }
    />
  );
}